import {
  AddSeatsModalConfirmed,
  ApplePayButtonClicked,
  ApplePaySetupFailed,
  BaseTrackingAdapter,
  CheckoutFlowCompleted,
  CheckoutFlowFailed,
  CheckoutFlowIssue,
  CheckoutFlowLoaded,
  CheckoutFlowLoadError,
  DeeplinkAutoOpenToggled,
  DeeplinkButtonClicked,
  DeeplinkButtonShown,
  DeeplinkGoToDownload,
  DeeplinkModalClosed,
  DeeplinkModalOpened,
  DeeplinkOpenInitiated,
  DeeplinkRetryButtonClicked,
  ElementClicked,
  ErrorShown,
  GoToCollaborationPromo,
  GoToDashboard,
  GoToMembers,
  GoToReviewLinks,
  GoToSharedWithMe,
  GoToSpace,
  GoToTeam,
  IntentPageFinished,
  IntentPageLoaded,
  InviteModalClosed,
  InviteModalEmailAdded,
  InviteModalEmailRemoved,
  InviteModalEstimateReceived,
  InviteModalInvalidEmailAdded,
  InviteModalShown,
  InviteModalSubmitted,
  ModalClosed,
  ModalShown,
  PageVisited,
  PaywallModalDismissed,
  PaywallModalShown,
  PaywallSubscribeClicked,
  PriceFetched,
  type ProgressiveProfilingCompleted,
  type ProgressiveProfilingStepCreatorsCompleted,
  type ProgressiveProfilingStepDesignStorageCompleted,
  type ProgressiveProfilingStepIndustryCompleted,
  type ProgressiveProfilingStepMainCadCompleted,
  type ProgressiveProfilingStepRoleCompleted,
  type ProgressiveProfilingStepViewersCompleted,
  ProjectPageAccessRequestAccepted,
  ProjectPageAccessRequested,
  ProjectPageAccessRequestRejected,
  ProjectPageAccessSwitchUser,
  ProjectPageDownloadFileClicked,
  ProjectPageDownloadFileShown,
  ProjectPageFeatureTourClose,
  ProjectPageFeatureTourCompleted,
  ProjectPageFeatureTourStepShown,
  ProjectPageLoaded,
  RenewalTooltipShown,
  RequestVerificationEmailClicked,
  type SegmentationSurveyCompletedV3,
  type SegmentationSurveyStepCadCompleted,
  type SegmentationSurveyStepFeaturesCompleted,
  type SegmentationSurveyStepIndustryCompleted,
  type SegmentationSurveyStepOrgSizeCompleted,
  type SegmentationSurveyStepRoleCompleted,
  type SegmentationSurveyStepUsageIntentCompleted,
  type SegmentationSurveyStepWorkCompleted,
  TelemetryClient,
  TwoFactorDisableModalAct,
  UpdatePaymentSourceSuccess,
  UrlChanged,
  UserLoggedIn,
  UserSignedUp,
  VideoStarted,
  VideoStopped,
  WebDashboardTeamSwitcherClicked,
  WebDashboardTeamSwitcherItemClicked,
  WebLinkClicked,
  WebLinkContextMenuActed,
  WebLinkContextMenuOpened,
  WebLinkCopied,
  WebLinkUnpublishConfirmed,
  WebShareModalEmailAdded,
  WebShareModalEmailRemoved,
  WebShareModalInvalidEmailAdded,
  WebShareModalInvitePressed,
  WebShareModalInviteRoleChanged,
  WebShareModalMemberRemoveConfirmed,
  WebShareModalMemberRemoveIntended,
  WebShareModalMemberRoleChanged,
  WebShareModalVisibilityChanged,
  WebViewerActiveToolChanged,
  WebViewerAnnotationPlaced,
  WebViewerCommentAdded,
  WebViewerCommentContextMenuVisibilityChanged,
  WebViewerCommentDeleteConfirmed,
  WebViewerCommentDeleteIntended,
  WebViewerCommentingDisplayed,
  WebViewerCommentingSidebarVisibilityChanged,
  WebViewerCommentSelected,
  WebViewerDrawingDisplayed,
  WebViewerDrawingDisplayFailed,
  WebViewerDrawingDownloaded,
  WebViewerDrawingDownloadFailed,
  WebViewerModelDisplayed,
  WebViewerModelDisplayFailed,
  WebViewerModelDownloaded,
  WebViewerModelDownloadFailed,
  WebViewerShareModalGeneralPermissionChanged,
  WebViewerShareModalGeneralRoleChanged,
  WebViewerViewLinkAccessDenied,
  WelcomeModalClosed,
  WelcomeModalShown,
} from "@shapr3d/frontend-lib";

import { telemetryClientEnvironment } from "../config";
import { version } from "../version";
import type { PaymentType } from "./ChargebeeAdapter";

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer: Record<string, any>[];
  }
}

interface GTMEvent {
  eventAction?: string;
  eventLabel?: string;
  eventValue?: string;
}

interface GAEvent extends GTMEvent {
  event: string;
  eventCategory?: string;
  clientIdNonAdvertising?: string;
  clientIdAdvertising?: string;
}

export const PaymentPlatforms = ["paddle", "chargebee"] as const;
export type PaymentPlatform = (typeof PaymentPlatforms)[number];

export type SegmentationSurveyEvents =
  | SegmentationSurveyCompletedV3
  | SegmentationSurveyStepCadCompleted
  | SegmentationSurveyStepFeaturesCompleted
  | SegmentationSurveyStepIndustryCompleted
  | SegmentationSurveyStepOrgSizeCompleted
  | SegmentationSurveyStepRoleCompleted
  | SegmentationSurveyStepUsageIntentCompleted
  | SegmentationSurveyStepWorkCompleted;

export type CheckoutFlowIssueType = CheckoutFlowIssue["eventProperties"]["type"];
export type CheckoutFlowFailedReason = CheckoutFlowFailed["eventProperties"]["reason"];
export type CheckoutFlowLoadErrorReason = CheckoutFlowLoadError["eventProperties"]["reason"];

export enum ElementClickAction {
  ADD_BILLING_DETAILS = "add_billing_details",
  ADD_COUPON_CODE = "add_coupon_code",
  ADD_SEATS = "add_seats",
  ADD_TO_SPACE = "add_to_space",
  APP_STORE_CHARGEBEE_TERMS_TOGGLE = "app_store_chargebee_terms_toggle",
  AUTO_RENEWAL_CANCEL_SUBSCRIPTION_CONFIRMATION = "auto_renewal_cancel_subscription_confirmation",
  CANCEL = "cancel",
  CANCEL_SEATS = "cancel_seats",
  CANCEL_SUBSCRIPTION = "cancel_subscription",
  CHANGE_BILLING_PERIOD = "change_billing_period",
  CHANGE_EMAIL = "change_email",
  CHANGE_MEMBERS_FILTER = "change_members_filter",
  CHANGE_NAME = "change_name",
  CHANGE_NUMBER_OF_SEATS = "change_number_of_seats",
  CHANGE_PASSWORD = "change_password",
  CHANGE_TO_TEAM = "change_to_team",
  CLEAR_MEMBERS_FILTER = "clear_members_filter",
  CONFIRM = "confirm",
  CREATE_SPACE = "create_space",
  DELETE_ACCOUNT_MODAL_OPENED = "delete_account_modal_opened",
  DESELECT_MEMBERS = "deselect_members",
  EDIT_BILLING_DETAILS = "edit_billing_details",
  EDIT_PAYMENT_INFORMATION = "edit_payment_information",
  FOCUS_MEMBERS_FILTER = "focus_members_filter",
  FORM_SUBMIT_ATTEMPT_WITH_ENTER = "form_submit_attempt_with_enter",
  GO_TO_ACCOUNT_SETTINGS = "go_to_account_settings",
  GO_TO_COMMUNITY = "go_to_community",
  GO_TO_CONTACT_SALES = "go_to_contact_sales",
  GO_TO_CONTACT_SUPPORT = "go_to_contact_support",
  GO_TO_DOWNLOAD = "go_to_download",
  GO_TO_HELP_CENTER = "go_to_help_center",
  GO_TO_LEARN_MORE = "go_to_learn_more",
  GO_TO_TEAMS_MARKETING = "go_to_teams_marketing",
  GO_TO_VIDEO_TUTORIALS = "go_to_video_tutorials",
  INVITE_MEMBERS = "invite_members",
  INVITE_WEB_MEMBERS = "invite_web_members",
  JOIN_TEAM = "join_team",
  LOG_OUT = "log_out",
  LEARN_MORE_PUBLISHING_VERSIONS = "learn_more_publishing_versions",
  MANAGE_AUTO_RENEWAL = "manage_auto_renewal",
  MANAGE_SEATS = "manage_seats",
  MANAGE_SEATS_ADD_SEATS_TO_TRIAL = "manage_seats_add_seats_to_trial",
  MANAGE_SEATS_BUY_SEATS = "manage_seats_buy_seats",
  OPEN_MEMBER_OPTIONS_MENU = "open_member_options_menu",
  OPEN_SHAPR = "open_shapr",
  OPEN_SPACE_OPTIONS_MENU = "open_space_options_menu",
  OPEN_TEAM_SWITCHER = "open_team_switcher",
  OPEN_TEAMS_VIDEO = "open_teams_video",
  OPEN_TOP_RIGHT_MENU = "open_top_right_menu",
  PURCHASE_NOTICE_COMPARE_SUBSCRIPTIONS = "purchase_notice_compare_subscriptions",
  PURCHASE_NOTICE_START_TRIAL = "purchase_notice_start_trial",
  PURCHASE_NOTICE_SUBSCRIBE_NOW = "purchase_notice_subscribe_now",
  REMOVE_FROM_SPACE = "remove_from_space",
  REMOVE_INVITATION = "remove_invitation",
  REMOVE_MEMBER = "remove_member",
  RENAME_SPACE = "rename_space",
  SEAT_COUNT_CHANGE = "seat_count_change",
  SELECT_MEMBERS = "select_members",
  SELECT_ROLE = "select_role",
  SELECT_SPACE = "select_space",
  START_EDU_FLOW = "start_edu_flow",
  START_SIGNUP_FLOW = "start_signup_flow",
  START_TRIAL_FLOW = "start_trial_flow",
  SUBMIT_CHECKOUT_FORM = "submit_checkout_form",
  SUBMIT_CHECKOUT_FORM_TRIAL = "submit_checkout_form_trial",
  SUBSCRIPTION_PERIOD_PICK = "subscription_period_pick",
  TEAM_NAME_EDIT_CHANGE = "team_name_edit_change",
  TURN_ON_AUTO_RENEWAL = "turn_on_auto_renewal",
  UPDATE_PAYMENT_INFORMATION = "update_payment_information",
  UPDATE_TEAM_NAME = "update_team_name",
  UPDATE_TEAM_NAME_SAVE = "update_team_name_save",
  //
  OPEN_ACCOUNT_MENU = "open_account_menu",
  CLOSE_ACCOUNT_MENU = "close_account_menu",
  DOWNLOAD_PDF = "download-pdf",
  NAVBAR_OPEN = "navbar_open",
  NAVBAR_CLOSE = "navbar_close",
  COPY_LINK = "copy_link",
  LOGIN = "login",
}

export enum ElementClickLocation {
  ADD_SEATS_MODAL = "add_seats_modal",
  ADD_TO_SPACE_MODAL = "add_to_space_modal",
  AUTO_RENEWAL_MANAGE_MENU = "auto_renewal_manage_menu",
  CANCEL_SEATS_MODAL = "cancel_seats_modal",
  CANCEL_SUBSCRIPTION_MODAL = "cancel_subscription_modal",
  CHANGE_EMAIL_MODAL = "change_email_modal",
  CHANGE_NAME_MODAL = "change_name_modal",
  CHANGE_PASSWORD_MODAL = "change_password_modal",
  CHECKOUT = "checkout",
  EDIT_BILLING_DETAILS_MODAL = "edit_billing_details_modal",
  EDIT_PAYMENT_INFORMATION_MODAL = "edit_payment_information_modal",
  HERO = "hero",
  INVITE_MEMBERS_MODAL = "invite_members_modal",
  MANAGE_SEATS_MENU = "manage_seats_menu",
  MEMBER_OPTIONS_MENU = "member_options_menu",
  MEMBERS_TABLE_FILTER_ROW = "members_table_filter_row",
  MEMBERS_TABLE_HEADER = "members_table_header",
  PROJECT_PAGE = "project_page",
  REMOVE_FROM_SPACE_MODAL = "remove_from_space_modal",
  REMOVE_MEMBER_MODAL = "remove_member_modal",
  SPACE_CREATION_MODAL = "space_creation_modal",
  SPACE_OPTIONS_MENU = "space_options_menu",
  SPACE_RENAME_MODAL = "space_rename_modal",
  TEAM_SWITCHER_MENU = "team_switcher_menu",
  TOP_RIGHT_MENU = "top_right_menu",
  WELCOME_MODAL = "welcome_modal",
  UPDATE_TEAM_NAME_MODAL = "update_team_name_modal",
  VIDEO_CAPTION = "video_caption",
  //
  SHARE_MODAL = "share_modal",
  HEADER = "header",
}

export enum ModalShownAction {
  TWO_FACTOR_ENABLE_INTENT = "two_factor_enable_intent",
  TWO_FACTOR_SETUP_SUCCESS = "two_factor_setup_success",
  TWO_FACTOR_RECOVERY_CODE = "two_factor_recovery_code",
  TWO_FACTOR_DISABLE = "two_factor_disable",
  ADD_SEAT_UNAVAILABLE = "add_seat_unavailable",
  APPLE_PAY_CONFIRMATION = "apple_pay_confirmation",
  INVITE_UNAVAILABLE = "invite_unavailable",
  PUBLISH_VERSION_MODAL = "publish_version_modal",
}

export enum ModalClosedAction {
  TWO_FACTOR_ENABLE_INTENT = "two_factor_enable_intent",
  TWO_FACTOR_SETUP_SUCCESS = "two_factor_setup_success",
  TWO_FACTOR_RECOVERY_CODE = "two_factor_recovery_code",
  TWO_FACTOR_DISABLE = "two_factor_disable",
  PUBLISH_VERSION_MODAL = "publish_version_modal",
}

export default class TrackingAdapter extends BaseTrackingAdapter {
  constructor(telemetryClient: TelemetryClient) {
    super(telemetryClient);
  }

  setUserData({ userID }: { userID: string | null }) {
    this.telemetryClient.setUserId(userID);
  }

  reset() {
    this.setUserData({ userID: null });
    this.telemetryClient.resetTrackingID();
  }

  private sendGAEvent(gaEvent: GAEvent) {
    if (window.dataLayer) {
      window.dataLayer.push(gaEvent);
    }
  }

  trackURLChanged = this.createTelemetryTrackingFunction(UrlChanged);

  trackPageView() {
    this.telemetryClient.trackTelemetryEvent(new PageVisited({ referrer: document.referrer }));
  }

  trackButtonClick({
    elementId,
    action,
    location,
    details,
  }: {
    /**
     * @deprecated
     */
    elementId?: string;
    action?: ElementClickAction;
    location?: string;
    details?: string;
  }) {
    this.telemetryClient.trackTelemetryEvent(
      new ElementClicked({ elementId, action, location, details })
    );
  }

  trackPriceFetched(duration: number) {
    this.telemetryClient.trackTelemetryEvent(new PriceFetched({ durationMs: duration }));
  }

  trackLoggedIn(appTrackingId?: string) {
    this.telemetryClient.trackTelemetryEvent(new UserLoggedIn({ appTrackingId }));
  }

  trackSignedUp(appTrackingId?: string) {
    this.telemetryClient.trackTelemetryEvent(new UserSignedUp({ type: "email", appTrackingId }));
    this.sendGAEvent({
      event: "GAEvent",
      eventCategory: "App signup web",
      eventAction: "Account creation sign up web",
      eventLabel: window.location.href,
    });
  }

  trackAutoRenewalTooltipOpened() {
    this.telemetryClient.trackTelemetryEvent(new RenewalTooltipShown());
  }

  trackCheckoutAbandonment() {
    this.sendGAEvent({
      event: "GAEvent",
      eventCategory: "Checkout",
      eventAction: "Abandon",
    });
  }

  trackInviteSuccess() {
    this.sendGAEvent({
      event: "GAEvent",
      eventCategory: "Invite modal",
      eventAction: "Success",
    });
  }

  trackInviteAbandonment(properties: ConstructorParameters<typeof InviteModalClosed>[0]) {
    this.telemetryClient.trackTelemetryEvent(new InviteModalClosed(properties));

    this.sendGAEvent({
      event: "GAEvent",
      eventCategory: "Invite modal",
      eventAction: "Abandon",
    });
  }

  trackPricingClickFromPricingPage() {
    this.sendGAEvent({
      event: "GAEvent",
      eventCategory: "Checkout",
      eventAction: "Initiate",
      eventLabel: "Buy now click - Pricing",
      eventValue: undefined,
    });
  }

  trackClientIDs(event: MessageEvent) {
    try {
      const analyticsData = JSON.parse(event.data);

      if (!analyticsData?.nonAdvertising || !analyticsData?.advertising) {
        return;
      }

      this.sendGAEvent({
        event: "clientIdDelivered",
        clientIdNonAdvertising: analyticsData.nonAdvertising.clientId,
        clientIdAdvertising: analyticsData.advertising.clientId,
      });
    } catch (err) {
      console.error(err);
    }
  }

  trackProgressiveProfilingEvent(
    event:
      | ProgressiveProfilingStepCreatorsCompleted
      | ProgressiveProfilingStepDesignStorageCompleted
      | ProgressiveProfilingStepIndustryCompleted
      | ProgressiveProfilingStepMainCadCompleted
      | ProgressiveProfilingStepRoleCompleted
      | ProgressiveProfilingStepViewersCompleted
      | ProgressiveProfilingCompleted
  ) {
    this.telemetryClient.trackTelemetryEvent(event);
  }

  trackSegmentationSurveyEvents(events: SegmentationSurveyEvents[]) {
    this.telemetryClient.trackTelemetryEvents(events);
  }

  trackCheckoutFlowLoaded(platform: PaymentPlatform) {
    this.telemetryClient.trackTelemetryEvent(new CheckoutFlowLoaded({ paymentPlatform: platform }));
  }

  trackCheckoutFlowLoadError(reason: CheckoutFlowLoadErrorReason) {
    this.telemetryClient.trackTelemetryEvent(new CheckoutFlowLoadError({ reason }));
  }

  trackCheckoutFlowCompleted(
    isTrial: boolean,
    platform: PaymentPlatform,
    seats: number,
    paymentType: PaymentType | undefined
  ) {
    this.sendGAEvent({
      event: "GAEvent",
      eventCategory: "Checkout",
      eventAction: "Payment Complete",
      eventValue: undefined,
    });
    this.telemetryClient.trackTelemetryEvent(
      new CheckoutFlowCompleted({ trial: isTrial, paymentPlatform: platform, seats, paymentType })
    );
  }

  trackCheckoutFlowIssue(isTrial: boolean, issueType: CheckoutFlowIssueType) {
    this.telemetryClient.trackTelemetryEvent(
      new CheckoutFlowIssue({
        trial: isTrial,
        paymentPlatform: "chargebee",
        type: issueType,
      })
    );
  }

  trackCheckoutFlowFailed(isTrial: boolean, reason: CheckoutFlowFailedReason, seats: number) {
    this.telemetryClient.trackTelemetryEvent(
      new CheckoutFlowFailed({
        trial: isTrial,
        paymentPlatform: "chargebee",
        reason,
        seats,
      })
    );
  }

  trackSegmentationSurveyGAEvent(value: string) {
    this.sendGAEvent({
      event: "GAEvent",
      eventCategory: "Segmentation Survey",
      eventAction: value,
    });
  }

  trackAddSeatsModalConfirmed(...props: ConstructorParameters<typeof AddSeatsModalConfirmed>) {
    this.telemetryClient.trackTelemetryEvent(new AddSeatsModalConfirmed(...props));
  }

  trackInviteModalEmailAdded = this.createTelemetryTrackingFunction(InviteModalEmailAdded);

  trackInviteModalInvalidEmailAdded = this.createTelemetryTrackingFunction(
    InviteModalInvalidEmailAdded
  );

  trackInviteModalEmailRemoved = this.createTelemetryTrackingFunction(InviteModalEmailRemoved);

  trackInviteModalSubmitted = this.createTelemetryTrackingFunction(InviteModalSubmitted);

  trackInviteModalError(details?: string) {
    this.telemetryClient.trackTelemetryEvent(
      new ErrorShown({
        action: ElementClickAction.INVITE_MEMBERS,
        location: ElementClickLocation.INVITE_MEMBERS_MODAL,
        details,
      })
    );
  }

  trackEmailChangeModalError(error?: unknown) {
    this.telemetryClient.trackTelemetryEvent(
      new ErrorShown({
        action: ElementClickAction.CHANGE_EMAIL,
        location: ElementClickLocation.CHANGE_EMAIL_MODAL,
        details: JSON.stringify(error),
      })
    );
  }

  trackNameChangeModalError(error?: string) {
    this.telemetryClient.trackTelemetryEvent(
      new ErrorShown({
        action: ElementClickAction.CHANGE_NAME,
        location: ElementClickLocation.CHANGE_NAME_MODAL,
        details: error,
      })
    );
  }

  trackModalShown(action: ModalShownAction, details?: string, location?: string) {
    this.telemetryClient.trackTelemetryEvent(
      new ModalShown({
        action,
        details,
        location,
      })
    );
  }

  trackModalClosed(action: ModalClosedAction, details?: string, location?: string) {
    this.telemetryClient.trackTelemetryEvent(
      new ModalClosed({
        action,
        details,
        location,
      })
    );
  }

  trackRequestVerificationEmailClicked = this.createTelemetryTrackingFunction(
    RequestVerificationEmailClicked
  );
  trackTwoFactorDisableAct = this.createTelemetryTrackingFunction(TwoFactorDisableModalAct);

  trackInviteModalShown = this.createTelemetryTrackingFunction(InviteModalShown);
  trackInviteModalEstimateReceived = this.createTelemetryTrackingFunction(
    InviteModalEstimateReceived
  );
  trackGoToReviewLinks = this.createTelemetryTrackingFunction(GoToReviewLinks);
  trackGoToSharedWithMe = this.createTelemetryTrackingFunction(GoToSharedWithMe);
  trackApplePayButtonClicked = this.createTelemetryTrackingFunction(ApplePayButtonClicked);
  trackUpdatePaymentSourceSuccess = this.createTelemetryTrackingFunction(
    UpdatePaymentSourceSuccess
  );
  trackApplePaySetupFailed = this.createTelemetryTrackingFunction(ApplePaySetupFailed);

  //

  trackModelDisplayed = this.createTelemetryTrackingFunction(WebViewerModelDisplayed);
  trackModelDisplayFailed = this.createTelemetryTrackingFunction(WebViewerModelDisplayFailed);
  trackModelDownloaded = this.createTelemetryTrackingFunction(WebViewerModelDownloaded);
  trackModelDownloadFailed = this.createTelemetryTrackingFunction(WebViewerModelDownloadFailed);
  trackDrawingDisplayed = this.createTelemetryTrackingFunction(WebViewerDrawingDisplayed);
  trackDrawingDisplayFailed = this.createTelemetryTrackingFunction(WebViewerDrawingDisplayFailed);
  trackDrawingDownloaded = this.createTelemetryTrackingFunction(WebViewerDrawingDownloaded);
  trackDrawingDownloadFailed = this.createTelemetryTrackingFunction(WebViewerDrawingDownloadFailed);
  trackAnnotationPlaced = this.createTelemetryTrackingFunction(WebViewerAnnotationPlaced);
  trackCommentAdded = this.createTelemetryTrackingFunction(WebViewerCommentAdded);
  trackCommentSelected = this.createTelemetryTrackingFunction(WebViewerCommentSelected);
  trackViewLinkAccessDenied = this.createTelemetryTrackingFunction(WebViewerViewLinkAccessDenied);
  trackActiveToolChanged = this.createTelemetryTrackingFunction(WebViewerActiveToolChanged);
  trackCommentingDisplayed = this.createTelemetryTrackingFunction(WebViewerCommentingDisplayed);
  trackCommentingSidebarVisibilityChanged = this.createTelemetryTrackingFunction(
    WebViewerCommentingSidebarVisibilityChanged
  );
  trackCommentContextMenuVisibilityChanged = this.createTelemetryTrackingFunction(
    WebViewerCommentContextMenuVisibilityChanged
  );
  trackCommentDeleteIntended = this.createTelemetryTrackingFunction(WebViewerCommentDeleteIntended);
  trackCommentDeleteConfirmed = this.createTelemetryTrackingFunction(
    WebViewerCommentDeleteConfirmed
  );
  trackShareModalGeneralPermissionChanged = this.createTelemetryTrackingFunction(
    WebViewerShareModalGeneralPermissionChanged
  );
  trackShareModalGeneralRoleChanged = this.createTelemetryTrackingFunction(
    WebViewerShareModalGeneralRoleChanged
  );
  trackShareModalVisibilityChanged = this.createTelemetryTrackingFunction(
    WebShareModalVisibilityChanged
  );
  trackShareModalMemberRoleChanged = this.createTelemetryTrackingFunction(
    WebShareModalMemberRoleChanged
  );
  trackShareModalMemberRemoveIntended = this.createTelemetryTrackingFunction(
    WebShareModalMemberRemoveIntended
  );
  trackShareModalMemberRemoveConfirmed = this.createTelemetryTrackingFunction(
    WebShareModalMemberRemoveConfirmed
  );
  trackShareModalInvitePressed = this.createTelemetryTrackingFunction(WebShareModalInvitePressed);
  trackShareModalInviteRoleChanged = this.createTelemetryTrackingFunction(
    WebShareModalInviteRoleChanged
  );
  trackShareModalEmailRemoved = this.createTelemetryTrackingFunction(WebShareModalEmailRemoved);
  trackShareModalEmailAdded = this.createTelemetryTrackingFunction(WebShareModalEmailAdded);
  trackShareModalInvalidEmailAdded = this.createTelemetryTrackingFunction(
    WebShareModalInvalidEmailAdded
  );
  trackDashboardTeamSwitcherClicked = this.createTelemetryTrackingFunction(
    WebDashboardTeamSwitcherClicked
  );
  trackDashboardTeamSwitcherItemClicked = this.createTelemetryTrackingFunction(
    WebDashboardTeamSwitcherItemClicked
  );
  trackLinkClicked = this.createTelemetryTrackingFunction(WebLinkClicked);
  trackLinkContextMenuActed = this.createTelemetryTrackingFunction(WebLinkContextMenuActed);
  trackLinkContextMenuOpened = this.createTelemetryTrackingFunction(WebLinkContextMenuOpened);
  trackLinkCopied = this.createTelemetryTrackingFunction(WebLinkCopied);
  trackGoToCollaborationPromo = this.createTelemetryTrackingFunction(GoToCollaborationPromo);
  trackGoToMembers = this.createTelemetryTrackingFunction(GoToMembers);
  trackGoToSpace = this.createTelemetryTrackingFunction(GoToSpace);
  trackGoToTeam = this.createTelemetryTrackingFunction(GoToTeam);
  trackGoToDashboard = this.createTelemetryTrackingFunction(GoToDashboard);
  trackUnpublishConfirmed = this.createTelemetryTrackingFunction(WebLinkUnpublishConfirmed);
  trackDownloadFileClicked = this.createTelemetryTrackingFunction(ProjectPageDownloadFileClicked);
  trackProjectPageDownloadFileShown = this.createTelemetryTrackingFunction(
    ProjectPageDownloadFileShown
  );
  trackProjectPageLoaded = this.createTelemetryTrackingFunction(ProjectPageLoaded);
  trackProjectPageAccessRequested = this.createTelemetryTrackingFunction(
    ProjectPageAccessRequested
  );
  trackProjectPageAccessRequestAccepted = this.createTelemetryTrackingFunction(
    ProjectPageAccessRequestAccepted
  );
  trackProjectPageAccessRequestRejected = this.createTelemetryTrackingFunction(
    ProjectPageAccessRequestRejected
  );
  trackProjectPageAccessSwitchUser = this.createTelemetryTrackingFunction(
    ProjectPageAccessSwitchUser
  );

  // Deep link events
  trackDeeplinkButtonShown = this.createTelemetryTrackingFunction(DeeplinkButtonShown);
  trackDeeplinkAutoOpenToggled = this.createTelemetryTrackingFunction(DeeplinkAutoOpenToggled);
  trackDeeplinkButtonClicked = this.createTelemetryTrackingFunction(DeeplinkButtonClicked);
  trackDeeplinkGoToDownload = this.createTelemetryTrackingFunction(DeeplinkGoToDownload);
  trackDeeplinkModalClosed = this.createTelemetryTrackingFunction(DeeplinkModalClosed);
  trackDeeplinkModalOpened = this.createTelemetryTrackingFunction(DeeplinkModalOpened);
  trackDeeplinkOpenInitiated = this.createTelemetryTrackingFunction(DeeplinkOpenInitiated);
  trackDeeplinkRetryButtonClicked = this.createTelemetryTrackingFunction(
    DeeplinkRetryButtonClicked
  );

  // Paywall events
  trackPaywallModalShown = this.createTelemetryTrackingFunction(PaywallModalShown);
  trackPaywallModalDismissed = this.createTelemetryTrackingFunction(PaywallModalDismissed);
  trackPaywallSubscribeClicked = this.createTelemetryTrackingFunction(PaywallSubscribeClicked);

  trackProjectPageFeatureTourStepShown = this.createTelemetryTrackingFunction(
    ProjectPageFeatureTourStepShown
  );
  trackProjectPageFeatureTourCompleted = this.createTelemetryTrackingFunction(
    ProjectPageFeatureTourCompleted
  );
  trackProjectPageFeatureTourClose = this.createTelemetryTrackingFunction(
    ProjectPageFeatureTourClose
  );

  trackIntentPageLoaded = this.createTelemetryTrackingFunction(IntentPageLoaded);
  trackIntentPageFinished = this.createTelemetryTrackingFunction(IntentPageFinished);
  trackVideoStarted = this.createTelemetryTrackingFunction(VideoStarted);
  trackVideoStopped = this.createTelemetryTrackingFunction(VideoStopped);

  trackWelcomeModalShown = this.createTelemetryTrackingFunction(WelcomeModalShown);
  trackWelcomeModalClosed = this.createTelemetryTrackingFunction(WelcomeModalClosed);
}

export const trackingAdapter = new TrackingAdapter(
  new TelemetryClient({
    appName: "webapp",
    siteVersion: version,
    environment: telemetryClientEnvironment,
    titleOverride: "Shapr3D",
  })
);
